import { useMemo, useState } from "react";
import type { MetricRow, Rankings, SliceMetrics } from "./data/types";

const PROVIDER_COLOR: Record<string, string> = {
  deepgram: "#10b981",
  cartesia: "#6366f1",
  assemblyai: "#f97316",
  elevenlabs: "#0ea5e9",
  xai: "#64748b",
};

const ACCENTS = ["american", "british", "indian", "arabic"];
const SNR_VIEW = [
  { key: "clean", label: "Clean", from: "by_condition" as const },
  { key: "5.0", label: "5 dB", from: "by_snr" as const },
  { key: "10.0", label: "10 dB", from: "by_snr" as const },
  { key: "15.0", label: "15 dB", from: "by_snr" as const },
];

const pct = (v: number | null | undefined) =>
  v == null ? "—" : `${(v * 100).toFixed(1)}%`;
const ms = (v: number | null | undefined) =>
  v == null ? "—" : `${Math.round(v)} ms`;

function dot(provider: string) {
  return (
    <span
      className="stt-dot"
      style={{ background: PROVIDER_COLOR[provider] ?? "#94a3b8" }}
    />
  );
}

type Source = "canonical" | "live";
type View = "overall" | "accent" | "snr";

export function Leaderboard({ rankings }: { rankings: Rankings }) {
  const [source, setSource] = useState<Source>("live");
  const [view, setView] = useState<View>("overall");

  const rows = source === "canonical" ? rankings.canonical.rows : rankings.live.rows;
  const liveOnly = source === "live";

  // nWER chart scale: pad 20% past the worst so bars never touch the edge.
  const maxNwer = useMemo(() => {
    const vals = rows.map((r) => r.nwer ?? 0);
    return Math.max(0.05, ...vals) * 1.2;
  }, [rows]);

  return (
    <div className="stt-leaderboard">
      <div className="stt-lb-toolbar">
        <div className="stt-segment">
          <button
            className={source === "canonical" ? "active" : ""}
            onClick={() => setSource("canonical")}
          >
            Reference benchmark
          </button>
          <button
            className={source === "live" ? "active" : ""}
            onClick={() => {
              setSource("live");
              setView("overall");
            }}
          >
            Live · arena
            <span className="stt-count-chip">{rankings.live.sample_count}</span>
          </button>
        </div>
        {!liveOnly ? (
          <div className="stt-segment stt-segment--ghost">
            <button className={view === "overall" ? "active" : ""} onClick={() => setView("overall")}>
              Overall
            </button>
            <button className={view === "accent" ? "active" : ""} onClick={() => setView("accent")}>
              By accent
            </button>
            <button className={view === "snr" ? "active" : ""} onClick={() => setView("snr")}>
              By noise
            </button>
          </div>
        ) : null}
        {source === "canonical" && rankings.canonical.timestamp ? (
          <span className="stt-lb-stamp">run {rankings.canonical.timestamp}</span>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="stt-empty">
          {liveOnly
            ? "No arena samples benchmarked yet. Add one in the Add Sample tab."
            : "No reference benchmark found."}
        </div>
      ) : view === "overall" ? (
        <OverallView rows={rows} maxNwer={maxNwer} />
      ) : view === "accent" ? (
        <MatrixView rows={rows} columns={ACCENTS.map((a) => ({ key: a, label: a }))} slice="by_accent" />
      ) : (
        <SnrMatrix rows={rows} />
      )}
    </div>
  );
}

function OverallView({ rows, maxNwer }: { rows: MetricRow[]; maxNwer: number }) {
  return (
    <>
      <div className="stt-chart">
        {rows.map((r) => (
          <div className="stt-chart-row" key={r.key}>
            <div className="stt-chart-label">
              {dot(r.provider)}
              {r.provider}
              <span className="stt-chart-model">{r.model}</span>
            </div>
            <div className="stt-chart-track">
              <div
                className="stt-chart-bar"
                style={{
                  width: `${Math.min(100, ((r.nwer ?? 0) / maxNwer) * 100)}%`,
                  background: PROVIDER_COLOR[r.provider] ?? "#94a3b8",
                }}
              />
              <span className="stt-chart-val">{pct(r.nwer)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="stt-table-wrap">
        <table className="stt-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Provider</th>
              <th>Model</th>
              <th className="num">nWER</th>
              <th className="num">CER</th>
              <th className="num">SER</th>
              <th className="num">TTFT</th>
              <th className="num">n</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key}>
                <td className="stt-rank">{i + 1}</td>
                <td>
                  <span className="stt-prov">{dot(r.provider)}{r.provider}</span>
                </td>
                <td className="stt-model">{r.model}</td>
                <td className="num stt-strong">{pct(r.nwer)}</td>
                <td className="num">{pct(r.cer)}</td>
                <td className="num">{pct(r.ser)}</td>
                <td className="num">{ms(r.ttft_ms)}</td>
                <td className="num stt-muted">{r.n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function sliceVal(row: MetricRow, slice: "by_accent" | "by_condition" | "by_snr", key: string): number | undefined {
  const s = row[slice]?.[key] as SliceMetrics | undefined;
  return s?.mean_nwer;
}

function MatrixView({
  rows,
  columns,
  slice,
}: {
  rows: MetricRow[];
  columns: { key: string; label: string }[];
  slice: "by_accent" | "by_condition" | "by_snr";
}) {
  return (
    <div className="stt-table-wrap">
      <table className="stt-table stt-matrix">
        <thead>
          <tr>
            <th>Model</th>
            {columns.map((c) => (
              <th key={c.key} className="num stt-cap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>
                <span className="stt-prov">{dot(r.provider)}{r.provider}</span>
                <span className="stt-model"> {r.model}</span>
              </td>
              {columns.map((c) => (
                <td key={c.key} className="num">
                  <HeatCell v={sliceVal(r, slice, c.key)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SnrMatrix({ rows }: { rows: MetricRow[] }) {
  return (
    <div className="stt-table-wrap">
      <table className="stt-table stt-matrix">
        <thead>
          <tr>
            <th>Model</th>
            {SNR_VIEW.map((c) => (
              <th key={c.key} className="num stt-cap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>
                <span className="stt-prov">{dot(r.provider)}{r.provider}</span>
                <span className="stt-model"> {r.model}</span>
              </td>
              {SNR_VIEW.map((c) => (
                <td key={c.key} className="num">
                  <HeatCell v={sliceVal(r, c.from, c.key)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Color-graded nWER cell: green (good) → amber → red (bad), 0–60% range.
function HeatCell({ v }: { v: number | undefined }) {
  if (v == null) return <span className="stt-muted">—</span>;
  const t = Math.min(1, v / 0.6);
  const hue = 140 - t * 140; // 140 (green) → 0 (red)
  return (
    <span className="stt-heat" style={{ background: `hsl(${hue} 70% 92%)`, color: `hsl(${hue} 60% 28%)` }}>
      {pct(v)}
    </span>
  );
}
